import { ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  FileText,
  GraduationCap,
  Home,
  LogOut,
  LucideIcon,
  Menu as MenuIcon,
  PanelLeftClose,
  PanelLeftOpen,
  Receipt,
  Search,
  Settings,
} from "lucide-react";
import { persistor, RootState, useAppDispatch } from "../../store";
import { setMenuIndex } from "../../store/userSlice";
import { UserRole } from "lib/types";
import { cn } from "lib/utils";
import { Sheet, SheetContent, SheetTrigger } from "components/ui/sheet";

type NavItem = { index: number; label: string; url: string; icon: LucideIcon };
type NavGroup = { label: string; items: NavItem[] };

const COLLAPSED_KEY = "salesonline:sidebar-collapsed";

const buildGroups = (role: UserRole, name: string): NavGroup[] => {
  const showUniversity = process.env.REACT_APP_IS_SHOW_UNIVERSITY === "true";

  if (role === UserRole.USER) {
    return [
      {
        label: "영업",
        items: [
          { index: 0, label: "홈", url: "/school", icon: Home },
          { index: 1, label: "학교검색", url: "/school/search", icon: Search },
        ],
      },
      {
        label: "계정",
        items: [
          { index: 3, label: name, url: "/admin/setting", icon: Settings },
        ],
      },
    ];
  }

  const manage: NavItem[] = [
    {
      index: 5,
      label: role === UserRole.ADMIN ? "관리자" : name,
      url: "/admin/setting",
      icon: Settings,
    },
  ];
  if (showUniversity) {
    manage.push({
      index: 6,
      label: "학과현황",
      url: "/university",
      icon: GraduationCap,
    });
  }

  return [
    {
      label: "영업",
      items: [
        { index: 0, label: "홈", url: "/school", icon: Home },
        { index: 1, label: "학교검색", url: "/school/search", icon: Search },
        { index: 2, label: "견적요청", url: "/school/estimate", icon: FileText },
        {
          index: 3,
          label: "영업현황",
          url: "/school/sales-total",
          icon: BarChart3,
        },
      ],
    },
    {
      label: "정산",
      items: [
        {
          index: 4,
          label: "세무•입금",
          url: "/school/tax/issue",
          icon: Receipt,
        },
      ],
    },
    { label: "관리", items: manage },
  ];
};

const roleName = (role: UserRole) =>
  role === UserRole.ADMIN
    ? "관리자"
    : role === UserRole.MANAGER
    ? "매니저"
    : "사용자";

function AppShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userState = useSelector((state: RootState) => state.user);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSED_KEY) === "true"
  );

  useEffect(() => {
    localStorage.setItem(COLLAPSED_KEY, String(collapsed));
  }, [collapsed]);

  const isLoggedIn =
    userState.role === UserRole.USER ||
    userState.role === UserRole.MANAGER ||
    userState.role === UserRole.ADMIN;

  const onLogout = async () => {
    await persistor.purge();
    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("refreshToken");
    setMobileOpen(false);
    navigate("/");
  };

  const navigateUrl = (item: NavItem) => {
    dispatch(setMenuIndex(item.index));
    setMobileOpen(false);
    navigate(item.url);
  };

  // 로그인 전 화면과 외부 견적요청 화면은 내비게이션 없이 전체 화면을 사용한다.
  if (!isLoggedIn || location.pathname === "/estimate") {
    return <>{children}</>;
  }

  const name = userState.me?.name ?? "";
  const groups = buildGroups(userState.role, name);

  const renderItem = (item: NavItem, isCollapsed: boolean) => {
    const active = userState.menuIndex === item.index;
    const Icon = item.icon;
    return (
      <button
        key={`${item.index}-${item.label}`}
        type="button"
        title={isCollapsed ? item.label : undefined}
        onClick={() => navigateUrl(item)}
        className={cn(
          "group relative flex w-full items-center gap-3 rounded-lg py-2 text-sm transition-colors",
          isCollapsed ? "justify-center px-0" : "px-3",
          active
            ? "bg-indigo-50 font-semibold text-indigo-700"
            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        )}
      >
        {active && (
          <span className="absolute bottom-1.5 left-0 top-1.5 w-[3px] rounded-full bg-indigo-600" />
        )}
        <Icon
          className={cn(
            "h-[18px] w-[18px] shrink-0",
            active
              ? "text-indigo-600"
              : "text-gray-400 group-hover:text-gray-600"
          )}
        />
        {!isCollapsed && <span className="truncate">{item.label}</span>}
      </button>
    );
  };

  const renderNav = (isCollapsed: boolean) => (
    <nav className="flex-1 overflow-y-auto px-2 py-3">
      {groups
        .filter((g) => g.items.length > 0)
        .map((g) => (
          <div key={g.label} className="pb-3">
            {!isCollapsed && (
              <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                {g.label}
              </div>
            )}
            <div className="flex flex-col gap-0.5">
              {g.items.map((item) => renderItem(item, isCollapsed))}
            </div>
          </div>
        ))}
    </nav>
  );

  const renderBrand = (isCollapsed: boolean) => (
    <div
      className={cn(
        "flex h-14 shrink-0 items-center border-b border-gray-200",
        isCollapsed ? "justify-center px-0" : "px-4"
      )}
    >
      <button
        type="button"
        className="flex min-w-0 items-center gap-2"
        onClick={() => navigate("/")}
      >
        <img src="/logo.svg" alt="Logo" width={26} height={26} />
        {!isCollapsed && (
          <span className="truncate text-[15px] font-bold tracking-tight text-gray-900">
            SalesOnline
          </span>
        )}
      </button>
    </div>
  );

  const renderAccount = (isCollapsed: boolean) => (
    <div className="shrink-0 border-t border-gray-200 p-2">
      <div
        className={cn(
          "flex items-center gap-2 rounded-lg px-2 py-2",
          isCollapsed && "justify-center px-0"
        )}
      >
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white"
          title={name}
        >
          {name.slice(0, 1)}
        </div>
        {!isCollapsed && (
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-gray-900">
              {name}
            </div>
            <div className="truncate text-xs text-gray-500">
              {roleName(userState.role)}
            </div>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onLogout}
        title={isCollapsed ? "로그아웃" : undefined}
        className={cn(
          "flex w-full items-center gap-3 rounded-lg py-2 text-sm text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900",
          isCollapsed ? "justify-center px-0" : "px-3"
        )}
      >
        <LogOut className="h-[18px] w-[18px] shrink-0 text-gray-400" />
        {!isCollapsed && <span>로그아웃</span>}
      </button>
    </div>
  );

  return (
    <div className="flex min-h-screen w-full">
      {/* 데스크탑 사이드바 */}
      <aside
        className={cn(
          "sticky top-0 hidden h-screen shrink-0 flex-col border-r border-gray-200 bg-gray-50 transition-[width] duration-200 lg:flex",
          collapsed ? "w-16" : "w-60"
        )}
      >
        {renderBrand(collapsed)}
        {renderNav(collapsed)}
        {renderAccount(collapsed)}
        <div className="shrink-0 border-t border-gray-200 p-2">
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            aria-label={collapsed ? "메뉴 펼치기" : "메뉴 접기"}
            className={cn(
              "flex w-full items-center gap-3 rounded-lg py-2 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900",
              collapsed ? "justify-center px-0" : "px-3"
            )}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-[18px] w-[18px] shrink-0" />
            ) : (
              <>
                <PanelLeftClose className="h-[18px] w-[18px] shrink-0" />
                <span>메뉴 접기</span>
              </>
            )}
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col bg-white">
        {/* 모바일 상단바 */}
        <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-gray-200 bg-white/95 px-4 backdrop-blur lg:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label="메뉴 열기"
                className="inline-flex h-10 w-10 items-center justify-center rounded-md text-gray-700 hover:bg-gray-100"
              >
                <MenuIcon className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[272px] bg-gray-50 p-0">
              <div className="flex h-full flex-col">
                {renderBrand(false)}
                {renderNav(false)}
                {renderAccount(false)}
              </div>
            </SheetContent>
          </Sheet>
          <button
            type="button"
            className="flex items-center gap-2"
            onClick={() => navigate("/")}
          >
            <img src="/logo.svg" alt="Logo" width={26} height={26} />
            <span className="text-[15px] font-bold tracking-tight text-gray-900">
              SalesOnline
            </span>
          </button>
        </header>

        <main className="min-w-0 flex-1">{children}</main>
      </div>
    </div>
  );
}

export default AppShell;
